import type { MissionDefinition } from './MissionTypes';
import type { ShipUpgrades } from '../upgrades';

/** External handles the tutorial steps need to introspect. Kept tiny —
 *  only the few systems whose state can be already-satisfied at mission
 *  start (e.g. faction preset that owns an upgrade, or a player who
 *  arrives at the gather step already carrying enough cargo). */
export interface TutorialMissionDeps {
  readonly shipUpgrades: ShipUpgrades;
  /** Combined sellable value across ship + suit inventories. Read at
   *  step activation time so the gather-then-sell flow auto-skips
   *  when the player already has enough. */
  readonly getInventoryValue: () => number;
  /** Current high-level game mode — used by the long-range tutorial
   *  to skip the "return to space" step when the player is already
   *  flying. Typed as `string` to accept any future mode without
   *  forcing the missions module to import the GameModeId type. */
  readonly getMode: () => string;
  /** Current fuel ratio (0..1). Lets the refuel mission skip when
   *  the player is already topped up. */
  readonly getFuelRatio: () => number;
}

const SHIP_MINING_BEAM_PRICE = 1200;
const HAND_MINING_CUTTER_PRICE = 800;
/** Long-range tutorial destination. The first hyperspace jump goes
 *  Caelis → Nyara so the trip is a clear inter-planetary hop, the
 *  scale of the system reads, and the destination is visually
 *  distinct (mint storm world). Must match a `MapBody.id` —
 *  `displayName` is the id source in the MainScene wiring. */
const FIRST_JUMP_TARGET = 'Nyara';
/** Step id whose activation triggers the GameDirector's scripted
 *  fuel drain in mission 10. Kept as a public-ish constant so the
 *  mission file and the director read the same string. */
const AUTONOMY_BURN_STEP_ID = 'autonomy-test-burn';

/**
 * The tutorial mission sequence, in order. Player-facing text is in
 * English by design — see CLAUDE.md / direction.
 *
 * Each mission auto-starts when the previous finishes (the manager
 * advances through this array). The first one auto-starts on a fresh
 * game. Steps with `skipIf` may auto-complete when their goal is
 * already met (e.g. the Miner faction starts with the Industrial
 * Mining Beam preinstalled, so the buy-step skips).
 */
export function buildTutorialMissions(
  deps: TutorialMissionDeps
): readonly MissionDefinition[] {
  const ownsBeam = (): boolean => deps.shipUpgrades.has('ship-mining-beam');
  const ownsHandCutter = (): boolean =>
    deps.shipUpgrades.has('fps-mining-tool');
  const hasCargoFor = (price: number) => (): boolean =>
    deps.getInventoryValue() >= price;
  const REFUEL_TARGET_RATIO = 0.8;
  const fuelAtLeastTarget = (): boolean =>
    deps.getFuelRatio() >= REFUEL_TARGET_RATIO;

  return [
    {
      id: 'signal-arrival',
      title: 'Signal Arrival',
      description:
        'Your ship limped into the sector on fumes. Dock at Starfall Station and top up the tank before anything dramatic happens.',
      autoStart: true,
      isTutorial: true,
      completionMessage:
        'Tank topped up. Registration mostly accepted. Enough for nobody to shoot first.',
      steps: [
        {
          id: 'reach-station',
          objectiveText: 'Reach Starfall Station.',
          hintText:
            'Fuel reserves are at 30%. Approach the station and press E to dock.',
          waypointTarget: 'station',
          event: 'stationEntered'
        },
        {
          id: 'refuel-at-engineering',
          objectiveText: 'Refuel your ship at the engineering counter.',
          hintText:
            'Find the engineering counter inside the station and pay for a refill.',
          waypointTarget: 'station',
          event: 'shipRefueled',
          predicate: (payload: unknown) => {
            const data = payload as { amount?: number };
            return (data.amount ?? 0) > 0;
          }
        }
      ]
    },
    {
      id: 'aggressive-mining',
      title: 'Aggressive Mining',
      description:
        'Without the proper tool, your best option is to negotiate with asteroids through loud and unnecessary violence.',
      isTutorial: true,
      completionMessage: 'Congratulations. You invented mining through violence.',
      steps: [
        {
          id: 'collect-fifteen-resources',
          objectiveText:
            'Leave the station, return to space, and break asteroids to collect 15 resources.',
          hintText:
            'Walk to the exit pad inside the station and press E to undock. Once in space, fire the ship cannons with LMB on nearby asteroids. It works, but it is slow and deeply inelegant.',
          requiredCount: 15,
          event: 'asteroidLootCollected',
          increment: (payload: unknown) => {
            const data = payload as { count?: number };
            return Math.max(1, data.count ?? 1);
          }
        }
      ]
    },
    {
      id: 'sell-your-shame',
      title: 'Sell Your Shame',
      description: 'Turn mistreated minerals into almost-honest credits.',
      isTutorial: true,
      completionMessage:
        'Credits received. Your criminal career now has cash flow.',
      steps: [
        {
          id: 'sell-anything',
          objectiveText: 'Sell any resource at the Market.',
          hintText: 'Enter the station and find the market counter.',
          waypointTarget: 'station',
          event: 'tradeSaleCompleted',
          predicate: (payload: unknown) => {
            const data = payload as { quantity?: number };
            return (data.quantity ?? 0) > 0;
          }
        }
      ]
    },
    {
      id: 'laser-solves-it',
      title: 'Laser Solves It',
      description:
        'Cannons can break rocks, but a professional criminal invests in cleaner tools. Stack enough cargo for the upgrade, then come home rich enough to install it.',
      isTutorial: true,
      completionMessage: 'Now that looked almost professional.',
      steps: [
        {
          id: 'gather-for-beam',
          objectiveText:
            'Mine the Verdia asteroid belt until your cargo is worth at least 1,200 cr.',
          hintText:
            'Fly out to the belt orbiting Verdia and break asteroids with LMB. The belt is denser than the rocks near the station — easier to fill the hold there.',
          waypointTarget: 'Verdia',
          // Absolute progress against the buy price — the tracker HUD
          // shows a 0…1200 bar so the player knows how close they are.
          requiredCount: SHIP_MINING_BEAM_PRICE,
          event: 'inventoryValueChanged',
          absoluteCount: (payload: unknown) => {
            const data = payload as { value?: number };
            return Math.floor(data.value ?? 0);
          },
          // Already-rich players (e.g. lots of leftover cargo from the
          // earlier missions) skip straight to selling.
          skipIf: hasCargoFor(SHIP_MINING_BEAM_PRICE)
        },
        {
          id: 'sell-haul-for-beam',
          objectiveText: 'Return to Starfall Station and sell your haul at the Market.',
          hintText:
            'Dock at the station, find the market counter, and convert your ore into credits.',
          waypointTarget: 'station',
          event: 'tradeSaleCompleted',
          predicate: (payload: unknown) => {
            const data = payload as { quantity?: number };
            return (data.quantity ?? 0) > 0;
          }
        },
        {
          id: 'buy-ship-mining-beam',
          objectiveText: 'Buy the Industrial Mining Beam in the Engineering Bay.',
          hintText:
            'Visit the engineering counter and install the beam (1,200 cr).',
          waypointTarget: 'station',
          event: 'shopItemPurchased',
          predicate: (payload: unknown) => {
            const data = payload as { upgradeId?: string };
            return data.upgradeId === 'ship-mining-beam';
          },
          // Miner faction ships with the beam factory-fitted — there
          // is nothing to buy, auto-complete.
          skipIf: ownsBeam
        },
        {
          id: 'mine-asteroid-with-beam',
          objectiveText: 'Leave the station and mine an asteroid with RMB.',
          hintText:
            'Undock, fly to a nearby asteroid and hold RMB to fire the Industrial Mining Beam.',
          event: 'asteroidLootCollected',
          predicate: (payload: unknown) => {
            const data = payload as { source?: string };
            return data.source === 'beam';
          }
        }
      ]
    },
    {
      id: 'caelis-the-dry-rose',
      title: 'Caelis, the Dry Rose',
      description:
        'A pale rose world of salt flats, shallow reflections and terrible hydration advice. Save first — long burns near the star end careers.',
      isTutorial: true,
      completionMessage:
        'Caelis. Too beautiful for a place trying to dry you out.',
      steps: [
        {
          id: 'save-the-game',
          objectiveText: 'Save your game before the long burn.',
          hintText:
            'Press Tab to open the pause menu and click Save Game. Long flights pass close to the star — better to keep a fallback.',
          event: 'gameSaved'
        },
        {
          id: 'land-on-caelis',
          objectiveText: 'Travel to Caelis and land on the surface.',
          hintText:
            'Open the system map (M) to set Caelis as destination if needed, approach the planet and press E to land.',
          waypointTarget: 'Caelis',
          event: 'planetLanded',
          predicate: (payload: unknown) => {
            const data = payload as { planetName?: string };
            return data.planetName === 'Caelis';
          }
        },
        {
          id: 'activate-caelis-mini-base',
          objectiveText:
            'Walk to the mini base on the surface and activate the terminal.',
          hintText:
            'Follow the mission marker on the minimap. Press E at the terminal to log your visit.',
          waypointTarget: 'caelis-base',
          event: 'surfaceTerminalActivated',
          predicate: (payload: unknown) => {
            const data = payload as { terminalId?: string };
            return data.terminalId === 'caelis-mini-base';
          }
        }
      ]
    },
    {
      id: 'hand-cutter',
      title: 'Hand Cutter',
      description:
        'Some rocks are too personal for ship-mounted equipment. Same drill: gather, sell, buy — then make the planet do the work for you.',
      isTutorial: true,
      completionMessage:
        'Surface mining confirmed. Your criminal career now has outdoor skills.',
      steps: [
        {
          id: 'gather-for-cutter',
          objectiveText:
            'Mine the Verdia asteroid belt until your cargo is worth at least 800 cr.',
          hintText:
            'Same belt orbiting Verdia. With the Industrial Mining Beam this trip should be quick.',
          waypointTarget: 'Verdia',
          event: 'inventoryValueChanged',
          predicate: (payload: unknown) => {
            const data = payload as { value?: number };
            return (data.value ?? 0) >= HAND_MINING_CUTTER_PRICE;
          },
          skipIf: hasCargoFor(HAND_MINING_CUTTER_PRICE)
        },
        {
          id: 'sell-haul-for-cutter',
          objectiveText: 'Return to Starfall Station and sell your haul at the Market.',
          hintText: 'Dock at the station and convert your ore at the market counter.',
          waypointTarget: 'station',
          event: 'tradeSaleCompleted',
          predicate: (payload: unknown) => {
            const data = payload as { quantity?: number };
            return (data.quantity ?? 0) > 0;
          }
        },
        {
          id: 'buy-hand-mining-cutter',
          objectiveText: 'Buy the Hand Mining Cutter at the Armory.',
          hintText: 'Visit the armory counter inside the station (800 cr).',
          waypointTarget: 'station',
          event: 'shopItemPurchased',
          predicate: (payload: unknown) => {
            const data = payload as { upgradeId?: string };
            return data.upgradeId === 'fps-mining-tool';
          },
          skipIf: ownsHandCutter
        },
        {
          id: 'mine-surface-deposit',
          objectiveText:
            'Land on Caelis, equip the cutter with 2, and mine a surface deposit.',
          hintText:
            'Tip: once landed, climb back into the ship and press Space — you can drive on the planet surface, much faster than walking to a deposit. Then dismount, press 2 to equip the cutter and hold LMB on a mineral deposit.',
          waypointTarget: 'Caelis',
          event: 'surfaceDepositMined',
          predicate: (payload: unknown) => {
            const data = payload as { count?: number };
            return (data.count ?? 0) > 0;
          }
        }
      ]
    },
    {
      id: 'open-map',
      title: 'Open Map',
      description:
        'The sector is bigger than your current financial planning.',
      isTutorial: true,
      completionMessage:
        'Destination plotted. Try not to think about the fuel bill.',
      steps: [
        {
          id: 'return-to-space-for-map',
          objectiveText: 'Return to space.',
          hintText:
            'Enter your ship and take off before plotting a long-range route.',
          // Player on Caelis after Hand Cutter — point them back at
          // the ship if they're on foot. In surface-ship mode the
          // hint covers it; the marker stays unset to avoid noise.
          event: 'returnedToSpace',
          // Skip when the player is already in space — the previous
          // mission may have ended there depending on how the player
          // closed it.
          skipIf: (): boolean => deps.getMode() === 'space-flying'
        },
        {
          id: 'open-system-map',
          objectiveText: 'Open the system map.',
          hintText: 'Press M to open the system map.',
          event: 'systemMapOpened'
        },
        {
          id: 'select-nyara-destination',
          objectiveText: `Click ${FIRST_JUMP_TARGET} on the system map to set it as your destination.`,
          hintText: `On the open map, left-click ${FIRST_JUMP_TARGET} (the distant mint storm world marked with the red mission ring) to select it as your destination.`,
          waypointTarget: FIRST_JUMP_TARGET,
          event: 'mapDestinationSelected',
          predicate: (payload: unknown) => {
            const data = payload as { destinationId?: string };
            return data.destinationId === FIRST_JUMP_TARGET;
          }
        }
      ]
    },
    {
      id: 'first-jump',
      title: 'First Jump',
      description:
        'Point the ship at the far end of your bad decisions and let the engine bend the rules.',
      isTutorial: true,
      completionMessage:
        'Hyperspace complete. Your ship is still in one piece, which is statistically encouraging.',
      steps: [
        {
          id: 'align-with-nyara',
          objectiveText: `Align your ship with ${FIRST_JUMP_TARGET}.`,
          hintText:
            'Turn the ship toward the mission marker until the jump path is aligned.',
          waypointTarget: FIRST_JUMP_TARGET,
          event: 'jumpTargetAligned',
          predicate: (payload: unknown) => {
            const data = payload as { destinationId?: string };
            return data.destinationId === FIRST_JUMP_TARGET;
          }
        },
        {
          id: 'charge-jump-to-nyara',
          objectiveText: 'Charge the hyperspace jump.',
          hintText: 'Press J to start charging the jump. Keep the ship aligned.',
          waypointTarget: FIRST_JUMP_TARGET,
          event: 'jumpChargeStarted',
          predicate: (payload: unknown) => {
            const data = payload as { destinationId?: string };
            return data.destinationId === FIRST_JUMP_TARGET;
          }
        },
        {
          id: 'complete-jump-to-nyara',
          objectiveText: `Complete the jump to ${FIRST_JUMP_TARGET}.`,
          hintText: 'Stay aligned and let the jump finish.',
          waypointTarget: FIRST_JUMP_TARGET,
          event: 'hyperspaceJumpCompleted',
          predicate: (payload: unknown) => {
            const data = payload as { destinationId?: string };
            return data.destinationId === FIRST_JUMP_TARGET;
          }
        }
      ]
    },
    {
      id: 'autonomy-test',
      title: 'Autonomy Test',
      description:
        'Starfall Station would like to run a very scientific fuel autonomy test. Nothing about this sounds suspicious.',
      isTutorial: true,
      completionMessage: 'Fuel depleted. Excellent data. Terrible situation.',
      steps: [
        {
          // Step id read by GameDirector to apply the scripted fuel
          // drain. Renaming this string requires updating
          // GameDirector.AUTONOMY_BURN_STEP_ID in tandem.
          id: AUTONOMY_BURN_STEP_ID,
          objectiveText: 'Run the autonomy burn until the tank is empty.',
          hintText:
            'Autonomy calibration in progress — your fuel is being drained on purpose. The station definitely planned this well.',
          event: 'fuelDepleted'
        }
      ]
    },
    {
      id: 'interstellar-tow',
      title: 'Interstellar Tow',
      description:
        'Sector Recovery specializes in rescuing pilots who confuse courage with fuel management.',
      isTutorial: true,
      completionMessage:
        'Tow complete. You are back at the station with just enough fuel to make another bad choice.',
      steps: [
        {
          id: 'open-radio-tab',
          objectiveText: 'Open the Radio tab.',
          hintText: 'Open your ship inventory (I) and switch to the Radio tab.',
          event: 'inventoryRadioOpened'
        },
        {
          id: 'request-rescue',
          objectiveText: 'Request rescue from Sector Recovery.',
          hintText: 'Click Request Rescue.',
          event: 'rescueRequested'
        },
        {
          id: 'wait-for-tug',
          objectiveText: 'Wait for the recovery tug.',
          hintText: 'Track the ETA on the rescue HUD and keep the ship steady.',
          waypointTarget: 'rescue-tug',
          event: 'rescueTugArrived'
        },
        {
          id: 'accept-tow',
          objectiveText: 'Accept the rescue tow.',
          hintText: 'Press E when the recovery tug is in position.',
          waypointTarget: 'rescue-tug',
          event: 'rescueTowAccepted'
        }
      ]
    },
    {
      id: 'refuel-before-trouble',
      title: 'Refuel Before Trouble',
      description:
        'You are back on station fuel, which is a polite way to say almost empty.',
      isTutorial: true,
      completionMessage:
        'Fuel restored. Now your bad decisions can cover longer distances.',
      steps: [
        {
          id: 'open-engineering-for-refuel',
          objectiveText: 'Go to the refuel point.',
          hintText:
            'Visit the Engineering Bay counter inside the station. Tip: You can also buy the Radioactive Recycler at the Engineering Bay and convert Uranium Ore into fuel from the Recycler tab in your inventory.',
          waypointTarget: 'station',
          event: 'stationServiceOpened',
          predicate: (payload: unknown) => {
            const data = payload as { service?: string };
            return data.service === 'ship-upgrade';
          },
          // Already topped up → skip the entire refuel mission.
          skipIf: fuelAtLeastTarget
        },
        {
          id: 'refuel-the-ship',
          objectiveText: 'Refuel your ship.',
          hintText:
            'Restore your fuel before heading back into deep space. Tip: Uranium Ore can become emergency fuel if you own the Radioactive Recycler.',
          waypointTarget: 'station',
          event: 'fuelRefilled',
          skipIf: fuelAtLeastTarget
        }
      ]
    },
    {
      id: 'unwanted-company',
      title: 'Unwanted Company',
      description:
        'A dark ship is stalking the Ambre route. It is either a threat, an opportunity, or both.',
      isTutorial: true,
      completionMessage:
        'Pirate down. Your cargo is safe, which is impressive considering how little of it exists.',
      steps: [
        {
          id: 'leave-station-for-ambush',
          objectiveText: 'Return to space — leave Starfall Station.',
          hintText:
            'Walk to the hangar exit pad and press E to undock. Then follow the mission marker toward Ambre.',
          event: 'stationExited',
          // Already in space → skip via the manager's skipIf chain.
          skipIf: (): boolean => deps.getMode() === 'space-flying'
        },
        {
          id: 'travel-to-ambre',
          objectiveText: 'Travel to Ambre.',
          hintText: 'Follow the mission marker to the peach world with the amber ring.',
          waypointTarget: 'Ambre',
          event: 'missionTargetReached',
          predicate: (payload: unknown) => {
            const data = payload as { token?: string };
            return data.token === 'Ambre';
          }
        },
        {
          id: 'investigate-signal',
          objectiveText: 'Investigate the hostile signature.',
          hintText: 'Approach the marked signal near Ambre.',
          waypointTarget: 'unwanted-signal',
          event: 'missionTargetReached',
          predicate: (payload: unknown) => {
            const data = payload as { token?: string };
            return data.token === 'unwanted-signal';
          }
        },
        {
          id: 'locate-pirate',
          objectiveText: 'Locate the black pirate ship.',
          hintText: 'The hostile signal has resolved into a ship. Keep it in sight.',
          waypointTarget: 'tutorial-pirate',
          event: 'missionTargetReached',
          predicate: (payload: unknown) => {
            const data = payload as { token?: string };
            return data.token === 'tutorial-pirate';
          }
        },
        {
          id: 'destroy-pirate',
          // No waypointTarget on purpose — once combat starts the
          // mission marker on the pirate would just block the actual
          // ship at close range. Clearing the marker keeps the
          // engagement readable.
          objectiveText: 'Destroy the pirate ship.',
          hintText:
            'Keep the target in your sights and fire until its hull breaks.',
          event: 'missionShipDestroyed',
          predicate: (payload: unknown) => {
            const data = payload as { missionTag?: string };
            return data.missionTag === 'tutorial-pirate';
          }
        },
        {
          id: 'collect-pirate-loot',
          objectiveText: 'Collect the dropped loot.',
          hintText: 'Loot has been added to your hold.',
          event: 'missionItemCollected',
          predicate: (payload: unknown) => {
            const data = payload as { kind?: string };
            return data.kind === 'pirate-loot';
          },
        }
      ]
    },
    {
      id: 'alternative-economics',
      title: 'Alternative Economics',
      description:
        'Now that you know how it feels to be robbed, let us explore the business model.',
      isTutorial: true,
      completionMessage: 'Cargo acquired. Ethics remain outside mission scope.',
      steps: [
        {
          id: 'locate-cargo-target',
          objectiveText: 'Locate the marked cargo target.',
          hintText: 'Follow the mission marker to the selected ship.',
          waypointTarget: 'cargo-target',
          event: 'missionTargetReached',
          predicate: (payload: unknown) => {
            const data = payload as { token?: string };
            return data.token === 'cargo-target';
          }
        },
        {
          id: 'destroy-cargo-target',
          // Marker stays on the cargo target through the destroy step
          // because the hauler runs from the fight — losing it without
          // a marker is too easy. The marker is rendered in compact
          // mode and offset above the health bar so it doesn't cover
          // the silhouette.
          objectiveText: 'Disable or destroy the cargo target.',
          hintText: 'Use your ship guns. Try to look professional.',
          waypointTarget: 'cargo-target',
          event: 'missionShipDestroyed',
          predicate: (payload: unknown) => {
            const data = payload as { missionTag?: string };
            return data.missionTag === 'cargo-target';
          }
        },
        {
          id: 'collect-stolen-cargo',
          objectiveText: 'Collect the stolen cargo.',
          hintText: 'Cargo has been added to your hold.',
          event: 'missionItemCollected',
          predicate: (payload: unknown) => {
            const data = payload as { kind?: string };
            return data.kind === 'cargo';
          }
        },
        {
          id: 'return-with-cargo',
          objectiveText: 'Return to Starfall Station.',
          hintText: 'Bring the cargo back before anyone writes a report.',
          waypointTarget: 'station',
          event: 'stationEntered'
        }
      ]
    },
    {
      id: 'hot-cargo',
      title: 'Hot Cargo',
      description:
        'Stolen cargo is only profit after someone else agrees to pretend it is merchandise.',
      isTutorial: true,
      completionMessage:
        'Cargo cleared. The paperwork is fictional, but the credits are real.',
      steps: [
        {
          id: 'open-market-counter',
          objectiveText: 'Go to the Market.',
          hintText: 'Find the market counter inside Starfall Station.',
          waypointTarget: 'station',
          event: 'stationServiceOpened',
          predicate: (payload: unknown) => {
            const data = payload as { service?: string };
            return data.service === 'trade-terminal';
          }
        },
        {
          id: 'sell-stolen-cargo',
          objectiveText: 'Sell the stolen cargo at the Market.',
          hintText: 'Use the Market counter to clear the cargo.',
          waypointTarget: 'station',
          event: 'tradeSaleCompleted',
          predicate: (payload: unknown) => {
            const data = payload as { quantity?: number };
            return (data.quantity ?? 0) > 0;
          }
        }
      ]
    },
    {
      id: 'open-contracts',
      title: 'Open Contracts',
      description: 'Welcome to freelance crime with better typography.',
      isTutorial: true,
      completionMessage: 'Tutorial complete. The sector is open.',
      steps: [
        {
          id: 'goto-mission-terminal',
          objectiveText: 'Go to the Mission Terminal.',
          hintText: 'Find the central mission terminal inside Starfall Station.',
          waypointTarget: 'station',
          event: 'missionTerminalOpened'
        },
        {
          id: 'review-terminal',
          objectiveText: 'Review the mission terminal.',
          hintText:
            'Open the terminal and review the Main Mission and Contracts tabs. Both are coming soon — the sector is yours to roam in the meantime.',
          event: 'missionTerminalOpened',
          // Auto-complete on the same beat the previous step ended:
          // there is nothing actionable in the terminal yet, so the
          // visual review is enough.
          skipIf: (): boolean => true
        }
      ]
    }
  ];
}
